import { useEffect, useMemo } from 'react';
import {
  useRecordingControls,
  useRecordingState,
  type RecordingInputEvent,
  type UseRecordingControlsReturn,
} from '@clarix/meeting-recording';
import type { ClaraRecordingStatus } from '@clarix/shared-types';
import { QM_LIMITS } from './constants';
import type { CalendarRecordingAdapter } from './adapter';

interface EngineBridgeProps {
  sessionKey: string;
  adapter: CalendarRecordingAdapter;
  dbName: string;
  onControls: (key: string, controls: UseRecordingControlsReturn) => void;
  onEngineState: (key: string, status: ClaraRecordingStatus, error: string | null) => void;
  /** Mic opened, switched, failed to switch, or lost: the shell's toasts and pickers. */
  onInputEvent: (event: RecordingInputEvent) => void;
}

/**
 * Lives inside a `<RecordingProvider key={sessionKey}>` (one engine per recording) and hands
 * the engine's controls and status up to the shell's recorder. Renders nothing.
 */
export function EngineBridge({ sessionKey, adapter, dbName, onControls, onEngineState, onInputEvent }: EngineBridgeProps) {
  const config = useMemo(
    () => ({ maxRecordingMinutes: QM_LIMITS.MAX_MINUTES, idbConfig: { dbName }, onInputEvent }),
    [dbName, onInputEvent],
  );
  const controls = useRecordingControls(adapter, config);
  const { status, error } = useRecordingState();

  // Every render: stop/pause/resume close over the engine's latest state.
  useEffect(() => {
    onControls(sessionKey, controls);
  });

  useEffect(() => {
    onEngineState(sessionKey, status, error);
  }, [sessionKey, status, error, onEngineState]);

  return null;
}
